// @agent-native/pinpoint — Express middleware for pin CRUD
// MIT License
//
// REST routes: GET (list), GET/:id, POST, PATCH/:id, DELETE/:id
// Path traversal validation on all IDs.

import { Router, type Request, type Response } from "express";

import { FileStore } from "../storage/file-store.js";
import { PinSchema } from "../storage/schemas.js";

const VALID_ID = /^[a-zA-Z0-9_-]+$/;

function validateId(id: string): boolean {
  return VALID_ID.test(id) && id.length > 0 && id.length <= 128;
}

export interface PinRoutesOptions {
  dataDir?: string;
}

export function pagePinRoutes(options: PinRoutesOptions = {}): Router {
  const router = Router();
  const store = new FileStore(options.dataDir || "data/pins");

  router.get("/", async (req: Request, res: Response) => {
    try {
      const pageUrl =
        typeof req.query.pageUrl === "string" ? req.query.pageUrl : undefined;
      const status = req.query.status
        ? ((typeof req.query.status === "string"
            ? req.query.status
            : undefined) as any)
        : undefined;
      const pins = await store.list({ pageUrl, status });
      res.json(pins);
    } catch (err) {
      res.status(500).json({ error: "Failed to list pins" });
    }
  });

  router.get("/:id", async (req: Request, res: Response) => {
    try {
      const id = String(req.params.id);
      if (!validateId(id)) {
        res.status(400).json({ error: "Invalid pin ID" });
        return;
      }
      const pins = await store.list();
      const pin = pins.find((p) => p.id === id);
      if (!pin) {
        res.status(404).json({ error: "Pin not found" });
        return;
      }
      res.json(pin);
    } catch (err) {
      res.status(500).json({ error: "Failed to get pin" });
    }
  });

  router.post("/", async (req: Request, res: Response) => {
    try {
      const result = PinSchema.safeParse(req.body);
      if (!result.success) {
        res
          .status(400)
          .json({ error: "Invalid pin data", details: result.error.issues });
        return;
      }
      await store.save(result.data as any);
      res.status(201).json(result.data);
    } catch (err) {
      res.status(500).json({ error: "Failed to create pin" });
    }
  });

  router.patch("/:id", async (req: Request, res: Response) => {
    try {
      const id = String(req.params.id);
      if (!validateId(id)) {
        res.status(400).json({ error: "Invalid pin ID" });
        return;
      }
      const result = PinSchema.partial().safeParse(req.body);
      if (!result.success) {
        res
          .status(400)
          .json({ error: "Invalid pin data", details: result.error.issues });
        return;
      }
      await store.update(id, result.data);
      res.json({ ok: true });
    } catch (err) {
      res.status(500).json({ error: "Failed to update pin" });
    }
  });

  router.delete("/:id", async (req: Request, res: Response) => {
    try {
      const id = String(req.params.id);
      if (!validateId(id)) {
        res.status(400).json({ error: "Invalid pin ID" });
        return;
      }
      await store.delete(id);
      res.json({ ok: true });
    } catch (err) {
      res.status(500).json({ error: "Failed to delete pin" });
    }
  });

  router.delete("/", async (req: Request, res: Response) => {
    try {
      const pageUrl =
        typeof req.query.pageUrl === "string" ? req.query.pageUrl : undefined;
      await store.clear(pageUrl);
      res.json({ ok: true });
    } catch (err) {
      res.status(500).json({ error: "Failed to clear pins" });
    }
  });

  return router;
}
